import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useToasts } from 'react-toast-notifications'
import groupApi from '_api/group'
import { joinGroupWithInvitation } from '_redux/modules/group'
import { GROUP_APPROVED } from '_utils/constant'

// Give the result toast a moment to be seen before leaving the page
const REDIRECT_DELAY_MS = 1400

// Joins a group right from the Find group page:
// open groups join instantly, others become a pending request, and groups with a
// question ask it first. approvalByGroupId maps group id -> UserGroup approval of the user.
const useFindGroupJoin = ({ approvalByGroupId, loadMembership }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { addToast } = useToasts()
  const [joiningGroupId, setJoiningGroupId] = useState(null)
  const [questionTarget, setQuestionTarget] = useState(null) // { group, question }
  const [answer, setAnswer] = useState('')

  const join = async (group, question, answerText) => {
    setJoiningGroupId(group.id)
    try {
      // Also refreshes myGroups so My Group shows the new group straight away
      await dispatch(
        joinGroupWithInvitation({ groupId: group.id, question, answer: answerText, passcode: '' })
      )
      // The join response doesn't say whether the request was auto-approved
      const membership = await loadMembership()
      if (membership[group.id] === GROUP_APPROVED) {
        const host = group.hostName ? ` — hosted by ${group.hostName}` : ''
        addToast(`You're in! You've joined ${group.name}${host}. 🎉`, {
          appearance: 'success',
          autoDismiss: true,
        })
      } else {
        addToast(`Request sent — awaiting approval for ${group.name}`, {
          appearance: 'warning',
          autoDismiss: true,
        })
      }
      setTimeout(() => history.push('/group/list-my-groups'), REDIRECT_DELAY_MS)
    } catch (error) {
      addToast(error?.message || 'Unable to join this group', {
        appearance: 'error',
        autoDismiss: true,
      })
    } finally {
      setJoiningGroupId(null)
    }
  }

  const startJoin = async (group) => {
    setJoiningGroupId(group.id)
    try {
      // The map list doesn't carry the group's join question, so fetch the latest one
      const { msgResp } = await groupApi.getById(group.id)
      if (msgResp?.question) {
        setJoiningGroupId(null)
        setAnswer('')
        setQuestionTarget({ group, question: msgResp.question })
        return
      }
      await join(group, null, null)
    } catch (error) {
      setJoiningGroupId(null)
      addToast(error?.msgResp || 'Unable to join this group', {
        appearance: 'error',
        autoDismiss: true,
      })
    }
  }

  const submitAnswer = () => {
    if (!answer.trim()) {
      addToast('Please answer the question first', { appearance: 'warning', autoDismiss: true })
      return
    }
    const { group, question } = questionTarget
    setQuestionTarget(null)
    join(group, question, answer.trim())
  }

  const closeQuestion = () => setQuestionTarget(null)

  const getAction = (group) => {
    const approval = approvalByGroupId[group.id]
    if (approval === GROUP_APPROVED) {
      return {
        label: 'Visit Group',
        onClick: () => history.push(`/group/list-events?groupId=${group.id}`),
      }
    }
    if (approval !== undefined) return { label: 'Pending approval', pending: true }
    if (joiningGroupId === group.id) return { label: 'Joining…', pending: true }
    return {
      label: group.autoApproval ? 'Join' : 'Request to Join',
      onClick: () => startJoin(group),
    }
  }

  return { getAction, questionTarget, answer, setAnswer, submitAnswer, closeQuestion }
}

export default useFindGroupJoin
